import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db54c61rqll58fj4ocf0";

type Status = "idle" | "loading" | "success" | "error";

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");

    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot !== "") {
      setStatus("success");
      formEl.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      const text = typeof value === "string" ? value : "";
      if (key === "website_alt" && text.trim() === "") {
        return;
      }
      payload.append(key, text);
    });

    setStatus("loading");
    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok =
        response.ok &&
        !isSpam &&
        (parsed?.code === "OK" || (parsed === null && response.ok));

      if (ok) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() !== ""
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，未能提交，请检查网络后重试。");
    }
  }

  return (
    <section id="subscribe" className="bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="grid items-center gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:grid-cols-2 md:p-10">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 font-label text-xs font-medium text-primary-800">
              <i className="ri-notification-3-line" aria-hidden="true"></i>
              更新订阅
            </span>
            <h2 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              订阅更新，新合集上线不错过
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              留下邮箱，我们会在每周整理出新的合集与榜单时通知你。也可以顺手选一个偏好题材，
              之后收到的内容会更贴近你的口味。
            </p>
            <div className="mt-5 flex flex-wrap gap-4 text-xs text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-accent-600" aria-hidden="true"></i>
                仅用于更新通知
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-accent-600" aria-hidden="true"></i>
                每周最多一封
              </span>
            </div>
          </div>

          <form
            data-readdy-form
            id="subscribe-form"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6"
          >
            <div className="flex flex-col gap-4">
              <div>
                <label
                  htmlFor="subscribe-email"
                  className="mb-1.5 block font-label text-xs font-medium text-foreground-700"
                >
                  邮箱地址
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div>
                <label
                  htmlFor="subscribe-interest"
                  className="mb-1.5 block font-label text-xs font-medium text-foreground-700"
                >
                  偏好题材（可选）
                </label>
                <select
                  id="subscribe-interest"
                  name="interest"
                  defaultValue=""
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  <option value="">不限，全部合集都接收</option>
                  <option value="日剧">日剧</option>
                  <option value="韩剧">韩剧</option>
                  <option value="日影">日影</option>
                  <option value="韩影">韩影</option>
                  <option value="动画">动画</option>
                  <option value="综艺">综艺</option>
                </select>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field-website-alt"
              />

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 cursor-pointer"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                    提交中
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" aria-hidden="true"></i>
                    订阅更新
                  </>
                )}
              </button>

              {status === "success" ? (
                <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                  订阅成功，新的合集上线后我们会第一时间通知你。
                </p>
              ) : null}

              {status === "error" ? (
                <p className="flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-900">
                  <i className="ri-error-warning-line" aria-hidden="true"></i>
                  {formError}
                </p>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}